/**
 * <WalletBalanceConverter> — Caja con switch toggle para ver el saldo de
 * una wallet expresado en UYU o en USDT/USD (según rol).
 *
 * NO modifica balances reales — solo cambia la representación visual usando
 * la `tasa_compra_usdt` global cargada al montar.
 *
 * Props:
 *   balanceUyu   : number (saldo en UYU)
 *   balanceUsdt  : number (saldo en USDT — para CEO/Proveedor) o USD para Gestor
 *   role         : 'admin'|'payment_agent'|'gestor'
 *   label        : string  título del card
 *   testid       : string
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeftRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { Money } from '@/components/Money';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const WalletBalanceConverter = ({
  balanceUyu = 0,
  balanceUsdt = 0,
  role = 'gestor',
  label = 'Saldo disponible',
  testid = 'wallet-balance-converter',
}) => {
  const { getAuthHeaders } = useAuth();
  const [tasa, setTasa] = useState(null);
  const [mode, setMode] = useState('native'); // 'native' | 'uyu' | 'usd'

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/usdt-config`, { headers: getAuthHeaders() });
        setTasa(Number(r.data?.tasa_compra_usdt) || null);
      } catch {
        setTasa(null);
      }
    })();
  }, [getAuthHeaders]);

  const isAdmin = role === 'admin' || role === 'payment_agent';
  const usdtLabel = isAdmin ? 'USDT' : 'USD';

  // Compute "shown" balance based on mode.
  let displayValue;
  let displayCcy;
  if (mode === 'native') {
    // Default: each wallet shows its own ccy.
    displayValue = balanceUyu + (tasa ? balanceUsdt * tasa : 0);
    displayCcy = 'UYU';
  } else if (mode === 'uyu') {
    displayValue = balanceUyu + (tasa ? balanceUsdt * tasa : 0);
    displayCcy = 'UYU';
  } else {
    // 'usd' mode → todo a USDT/USD usando la tasa.
    displayValue = (tasa ? balanceUyu / tasa : 0) + balanceUsdt;
    displayCcy = 'USDT';  // se renderizará "USDT" o "USD" según role-aware alias
  }

  return (
    <Card className="p-5 bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs uppercase tracking-wide text-emerald-800 font-bold">{label}</p>
        <button
          onClick={() => setMode((m) => (m === 'uyu' ? 'usd' : 'uyu'))}
          className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-emerald-200 text-emerald-700 text-xs font-medium hover:bg-emerald-50"
          data-testid={`${testid}-toggle`}
        >
          <ArrowLeftRight className="w-3 h-3" />
          Ver en {mode === 'uyu' ? usdtLabel : 'UYU'}
        </button>
      </div>
      <div className="text-3xl font-bold tracking-tight">
        <Money amount={displayValue} currency={displayCcy} role={role} sign="hide" bold />
      </div>
      {tasa && (
        <p className="text-[11px] text-emerald-700/70 mt-2">
          Convertido a la tasa del día: 1 {usdtLabel} = {tasa.toFixed(2)} UYU
        </p>
      )}
      <div className="flex gap-4 mt-3 pt-3 border-t border-emerald-100">
        <div>
          <p className="text-[10px] uppercase text-slate-500">UYU</p>
          <Money amount={balanceUyu} currency="UYU" role={role} sign="hide" className="text-sm" />
        </div>
        <div>
          <p className="text-[10px] uppercase text-slate-500">{usdtLabel}</p>
          <Money amount={balanceUsdt} currency="USDT" role={role} sign="hide" className="text-sm" />
        </div>
      </div>
    </Card>
  );
};

export default WalletBalanceConverter;
